import type { ReactNode } from 'react';
import { FontWeights } from '@newcore/ui/enums/font-weights';
import { Separator } from '@newcore/ui/separator';
import { Text } from '@newcore/ui/typography';
import { EmptyState } from '@/components/common/empty-state';
import { AppViewMode } from '@/enums/app-view-mode.enums';
import { RECENT_HEADING } from '@/features/applications/constants/applications.constants';
import type { ApplicationFilter } from '@/features/applications/types/applications.types';
import { emptyCatalogMessage } from '@/features/applications/utils/empty-catalog-message';
import type { UserApplication } from '@/types/api.types';
import { AppCollection, AppSkeletonCollection } from './app-collection';

interface CatalogBodyProps {
  viewMode: AppViewMode;
  isLoading: boolean;
  filter: ApplicationFilter;
  query: string;
  hasApplicationSearchQuery: boolean;
  matches: UserApplication[];
  recentApps: UserApplication[];
  recentAppIds: string[];
  showRecent: boolean;
  onApplicationClick: (application: UserApplication) => void;
  loadingAppId?: string | null;
}

export function CatalogBody({
  viewMode,
  isLoading,
  filter,
  query,
  hasApplicationSearchQuery,
  matches,
  recentApps,
  recentAppIds,
  showRecent,
  onApplicationClick,
  loadingAppId,
}: CatalogBodyProps) {
  if (isLoading) {
    const showRecentSkeleton =
      viewMode === AppViewMode.ICONS && recentAppIds.length > 0;

    return (
      <div className='flex w-full flex-col gap-6'>
        {showRecentSkeleton ? (
          <section className='flex w-full flex-col gap-3'>
            <SectionHeading>{RECENT_HEADING}</SectionHeading>
            <AppSkeletonCollection viewMode={viewMode} />
          </section>
        ) : (
          <AppSkeletonCollection viewMode={viewMode} />
        )}
      </div>
    );
  }

  if (matches.length === 0) {
    return (
      <EmptyState
        message={emptyCatalogMessage(filter, query, hasApplicationSearchQuery)}
      />
    );
  }

  if (hasApplicationSearchQuery) {
    return (
      <section className='flex w-full flex-col gap-3'>
        <SectionHeading>
          {matches.length} {matches.length === 1 ? 'result' : 'results'}
        </SectionHeading>
        <AppCollection
          apps={matches}
          viewMode={viewMode}
          onLaunch={onApplicationClick}
          loadingAppId={loadingAppId}
        />
      </section>
    );
  }

  return (
    <div className='flex w-full flex-col gap-6'>
      {showRecent && (
        <>
          <section className='flex w-full flex-col gap-3'>
            <SectionHeading>{RECENT_HEADING}</SectionHeading>
            <AppCollection
              apps={recentApps}
              viewMode={AppViewMode.ICONS}
              onLaunch={onApplicationClick}
              loadingAppId={loadingAppId}
            />
          </section>
          <Separator />
        </>
      )}

      <AppCollection
        apps={matches}
        viewMode={viewMode}
        onLaunch={onApplicationClick}
        loadingAppId={loadingAppId}
      />
    </div>
  );
}

function SectionHeading({ children }: { children: ReactNode }) {
  return (
    <Text as='h2' weight={FontWeights.MEDIUM} className='text-quaternary'>
      {children}
    </Text>
  );
}
